import { execute } from './apply';
export const EMPTY_HISTORY = { past: [], future: [] };
/** How far back undo can reach. Bounded so a long session cannot grow memory without limit. */
export const DEFAULT_HISTORY_LIMIT = 200;
/**
 * Consecutive edits with the same label and within this window merge into one step.
 *
 * Matches the typing-burst behaviour users expect: pause, and the next character starts a new
 * undo step.
 */
export const DEFAULT_COALESCE_WINDOW_MS = 800;
function canCoalesce(previous, next, windowMs) {
    if (previous?.label == null || next.label === null)
        return false;
    if (previous.label !== next.label)
        return false;
    return next.command.meta.timestamp - previous.command.meta.timestamp <= windowMs;
}
/**
 * Records an executed command.
 *
 * Clears the redo stack, because a new edit after an undo means the user chose a different
 * future and the old one is no longer reachable — keeping it would let redo overwrite work they
 * did deliberately.
 */
export function record(history, entry, options) {
    const limit = options?.limit ?? DEFAULT_HISTORY_LIMIT;
    const windowMs = options?.coalesceWindowMs ?? DEFAULT_COALESCE_WINDOW_MS;
    const previous = history.past[history.past.length - 1];
    if (canCoalesce(previous, entry, windowMs) && previous !== undefined) {
        // Keep the *first* inverse and the *latest* command: undoing the burst must return to where
        // it began, and redoing it must land where it ended.
        const merged = {
            command: entry.command,
            inverse: previous.inverse,
            label: entry.label,
        };
        return { past: [...history.past.slice(0, -1), merged], future: [] };
    }
    const past = [...history.past, entry];
    return { past: past.length > limit ? past.slice(past.length - limit) : past, future: [] };
}
export function canUndo(history) {
    return history.past.length > 0;
}
export function canRedo(history) {
    return history.future.length > 0;
}
/**
 * Undoes the most recent step.
 *
 * Returns `null` when there is nothing to undo, rather than throwing: an undo shortcut pressed
 * on an empty history is an ordinary thing for a person to do.
 */
export function undo(document, history) {
    const entry = history.past[history.past.length - 1];
    if (entry === undefined)
        return null;
    const result = execute(document, entry.inverse);
    return {
        document: result.document,
        history: { past: history.past.slice(0, -1), future: [entry, ...history.future] },
        applied: entry.inverse,
    };
}
export function redo(document, history) {
    const entry = history.future[0];
    if (entry === undefined)
        return null;
    const result = execute(document, entry.command);
    return {
        document: result.document,
        // The inverse is recomputed from the current document rather than reused: the redo has to
        // reverse *this* application, and the old inverse described a different starting state.
        history: {
            past: [...history.past, { ...entry, inverse: result.inverse }],
            future: history.future.slice(1),
        },
        applied: entry.command,
    };
}
/**
 * The same command, stamped as a new edit.
 *
 * An inverse carries the metadata of the command it reverses — including its `operationId` —
 * because that is the only metadata available at the moment it is built. Applied in memory that
 * is harmless; journalled, it is not. A consumer that de-duplicates by `operationId` (which the
 * offline journal does, and must, so a retry after a crash cannot double-apply an edit) sees the
 * undo as a repeat of the edit it undoes, and drops it. The user undoes something, reloads, and
 * finds it back.
 *
 * So an undo is journalled as what it actually is: a new edit, at the time it happened, with its
 * own identity. Re-stamping happens *before* the command is applied, so the document in memory
 * and the command in the journal agree about the timestamp written onto the nodes — otherwise
 * replay would produce a document that differs from the one the user is looking at.
 *
 * The same applies to a redo, which re-applies the original command a second time.
 */
export function restamp(command, meta) {
    return { ...command, meta };
}
/** Drops the history without touching the document — used when a map is closed or restored. */
export function clear() {
    return EMPTY_HISTORY;
}
/** Operation ids in the order they were applied, for reconciling against the sync outbox. */
export function appliedOperationIds(history) {
    return history.past.map((entry) => entry.command.meta.operationId);
}
//# sourceMappingURL=history.js.map